import React, { useState, useEffect } from 'react';
import { deviceAPI, nodeAPI } from '../services/api';
import './Dashboard.css';

function Dashboard() {
  const [devices, setDevices] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [stats, setStats] = useState({
    totalDevices: 0,
    activeDevices: 0,
    totalNodes: 0,
    activeNodes: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [expandedDevices, setExpandedDevices] = useState({});
  // Edit state
  const [editingDevice, setEditingDevice] = useState(null); // deviceId
  const [deviceEditForm, setDeviceEditForm] = useState({});
  const [editingNode, setEditingNode] = useState(null); // nodeId
  const [nodeEditForm, setNodeEditForm] = useState({});

  useEffect(() => {
    fetchData();
    // Refresh data every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [devicesRes, nodesRes] = await Promise.all([
        deviceAPI.getAll(),
        nodeAPI.getAll()
      ]);

      const devicesData = devicesRes.data.data || [];
      const nodesData = nodesRes.data.data || [];

      setDevices(devicesData);
      setNodes(nodesData);

      setStats({
        totalDevices: devicesData.length,
        activeDevices: devicesData.filter(d => d.status === 'active').length,
        totalNodes: nodesData.length,
        activeNodes: nodesData.filter(n => n.status === 'active').length
      });

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    if (window.confirm('Are you sure you want to delete this device and all its nodes?')) {
      try {
        await deviceAPI.delete(deviceId);
        fetchData();
      } catch (error) {
        alert('Error deleting device: ' + error.response?.data?.message);
      }
    }
  };

  const handleDeleteNode = async (nodeId) => {
    if (window.confirm('Are you sure you want to delete this node?')) {
      try {
        await nodeAPI.delete(nodeId);
        fetchData();
      } catch (error) {
        alert('Error deleting node: ' + error.response?.data?.message);
      }
    }
  };

  const handleEditDevice = (device) => {
    setEditingDevice(device.deviceId);
    setDeviceEditForm({
      deviceName: device.deviceName,
      description: device.description || '',
      ipAddress: device.ipAddress || '',
      location: device.location || ''
    });
  };

  const handleSaveDevice = async (deviceId) => {
    try {
      await deviceAPI.update(deviceId, deviceEditForm);
      setEditingDevice(null);
      fetchData();
    } catch (error) {
      alert('Error updating device: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEditNode = (node) => {
    setEditingNode(node.nodeId);
    setNodeEditForm({
      nodeName: node.nodeName,
      description: node.description || '',
      location: node.location || ''
    });
  };

  const handleSaveNode = async (nodeId) => {
    try {
      await nodeAPI.update(nodeId, nodeEditForm);
      setEditingNode(null);
      fetchData();
    } catch (error) {
      alert('Error updating node: ' + (error.response?.data?.message || error.message));
    }
  };

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  // Group nodes by deviceIdString
  const nodesByDevice = nodes.reduce((acc, node) => {
    if (!acc[node.deviceIdString]) acc[node.deviceIdString] = [];
    acc[node.deviceIdString].push(node);
    return acc;
  }, {});

  // Filter nodes by selected device
  const filteredNodes = selectedDeviceId
    ? nodes.filter((n) => n.deviceIdString === selectedDeviceId)
    : nodes;

  const handleDeviceExpand = (deviceId) => {
    setExpandedDevices((prev) => ({
      ...prev,
      [deviceId]: !prev[deviceId]
    }));
  };

  return (
    <div className="dashboard">
      <h2>System Dashboard</h2>

      {/* Device Selector */}
      <div style={{ marginBottom: 24 }}>
        <label htmlFor="deviceFilter" style={{ fontWeight: 600, marginRight: 8 }}>Filter by Device:</label>
        <select
          id="deviceFilter"
          value={selectedDeviceId}
          onChange={e => setSelectedDeviceId(e.target.value)}
          style={{ padding: 8, borderRadius: 6 }}
        >
          <option value="">All Devices</option>
          {devices.map(device => (
            <option key={device.deviceId} value={device.deviceId}>
              {device.deviceName} ({device.deviceId})
            </option>
          ))}
        </select>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">🖥️</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalDevices}</div>
            <div className="stat-label">Total Devices</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-value">{stats.activeDevices}</div>
            <div className="stat-label">Active Devices</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📡</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalNodes}</div>
            <div className="stat-label">Total Nodes</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">🟢</div>
          <div className="stat-content">
            <div className="stat-value">{stats.activeNodes}</div>
            <div className="stat-label">Active Nodes</div>
          </div>
        </div>
      </div>

      <div className="dashboard-section">
        <h3>Devices & Nodes</h3>
        {devices.length === 0 ? (
          <p className="no-items">No devices registered yet</p>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Device ID</th>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Location</th>
                  <th>IP Address</th>
                  <th>Last Seen</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {devices
                  .filter(device => !selectedDeviceId || device.deviceId === selectedDeviceId)
                  .map((device) => (
                    <React.Fragment key={device._id}>
                      <tr style={{ background: '#f6f8fa' }}>
                        <td>
                          <button
                            style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18 }}
                            onClick={() => handleDeviceExpand(device.deviceId)}
                            title={expandedDevices[device.deviceId] ? 'Hide Nodes' : 'Show Nodes'}
                          >
                            {expandedDevices[device.deviceId] ? '▼' : '▶'}
                          </button>
                        </td>
                        <td>{device.deviceId}</td>
                        <td>
                          {editingDevice === device.deviceId ? (
                            <input
                              value={deviceEditForm.deviceName}
                              onChange={e => setDeviceEditForm(f => ({ ...f, deviceName: e.target.value }))}
                              style={{ width: 120 }}
                            />
                          ) : device.deviceName}
                        </td>
                        <td>
                          <span className={`status-badge status-${device.status}`}>
                            {device.status}
                          </span>
                        </td>
                        <td>
                          {editingDevice === device.deviceId ? (
                            <input
                              value={deviceEditForm.location}
                              onChange={e => setDeviceEditForm(f => ({ ...f, location: e.target.value }))}
                              style={{ width: 100 }}
                            />
                          ) : (device.location || '-')}
                        </td>
                        <td>
                          {editingDevice === device.deviceId ? (
                            <input
                              value={deviceEditForm.ipAddress}
                              onChange={e => setDeviceEditForm(f => ({ ...f, ipAddress: e.target.value }))}
                              style={{ width: 110 }}
                            />
                          ) : (device.ipAddress || '-')}
                        </td>
                        <td>{new Date(device.lastSeen).toLocaleString()}</td>
                        <td>
                          {editingDevice === device.deviceId ? (
                            <>
                              <button className="save-btn" onClick={() => handleSaveDevice(device.deviceId)}>Save</button>
                              <button className="cancel-btn" onClick={() => setEditingDevice(null)}>Cancel</button>
                            </>
                          ) : (
                            <>
                              <button className="edit-btn" onClick={() => handleEditDevice(device)}>Edit</button>
                              <button className="delete-btn" onClick={() => handleDeleteDevice(device.deviceId)}>Delete</button>
                            </>
                          )}
                        </td>
                      </tr>
                      {expandedDevices[device.deviceId] && nodesByDevice[device.deviceId] && nodesByDevice[device.deviceId].length > 0 && (
                        <tr>
                          <td colSpan={8} style={{ padding: 0, background: '#fff' }}>
                            <table style={{ width: '100%', margin: 0, background: '#fff' }}>
                              <thead>
                                <tr style={{ background: '#f0f0f0' }}>
                                  <th>Node ID</th>
                                  <th>Name</th>
                                  <th>Status</th>
                                  <th>Location</th>
                                  <th>Last Data</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {nodesByDevice[device.deviceId].map((node) => (
                                  <tr key={node._id}>
                                    <td>{node.nodeId}</td>
                                    <td>
                                      {editingNode === node.nodeId ? (
                                        <input
                                          value={nodeEditForm.nodeName}
                                          onChange={e => setNodeEditForm(f => ({ ...f, nodeName: e.target.value }))}
                                          style={{ width: 100 }}
                                        />
                                      ) : (node.nodeName || node.meterName)}
                                    </td>
                                    <td>
                                      <span className={`status-badge status-${node.status}`}>
                                        {node.status}
                                      </span>
                                    </td>
                                    <td>
                                      {editingNode === node.nodeId ? (
                                        <input
                                          value={nodeEditForm.location}
                                          onChange={e => setNodeEditForm(f => ({ ...f, location: e.target.value }))}
                                          style={{ width: 90 }}
                                        />
                                      ) : (node.location || '-')}
                                    </td>
                                    <td>{new Date(node.lastDataReceived).toLocaleString()}</td>
                                    <td>
                                      {editingNode === node.nodeId ? (
                                        <>
                                          <button className="save-btn" onClick={() => handleSaveNode(node.nodeId)}>Save</button>
                                          <button className="cancel-btn" onClick={() => setEditingNode(null)}>Cancel</button>
                                        </>
                                      ) : (
                                        <>
                                          <button className="edit-btn" onClick={() => handleEditNode(node)}>Edit</button>
                                          <button className="delete-btn" onClick={() => handleDeleteNode(node.nodeId)}>Delete</button>
                                        </>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
